import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useOrganization } from "../App";
import { toast } from "@/components/ui/use-toast";
import { markAssistantPublished, publishAgent } from "@/services/agentPublishService";
import type {
  AgentPublishFeatureType,
  AgentPublishType,
  AgentPublishVersion,
} from "@/services/agentPublishService";

export interface HirevoxPublishOptions {
  type: AgentPublishType;
  featureType: AgentPublishFeatureType;
  version: AgentPublishVersion;
}

/**
 * The Publish action shared by the assistant editor and list: pushes to Hirevox, and marks the
 * assistant published only if that succeeds.
 *
 * Only organizations with the hirevox_publish flag (organizations.feature_flags) have Publish. The
 * flag is opt-in: a missing flag is off, unlike FeatureGuard and useBackendFlags.
 */
export function useAssistantPublish(logSource: string) {
  const { currentOrganization } = useOrganization();
  const [publishingAssistantId, setPublishingAssistantId] = useState<string | null>(null);

  const hirevoxEnabled = currentOrganization?.feature_flags?.hirevox_publish === true;

  // Resolves to whether it worked; a failure has already been reported to the user
  const publishAssistant = async (
    assistant: { id: string; name: string },
    hirevoxOptions: HirevoxPublishOptions
  ) => {
    setPublishingAssistantId(assistant.id);
    try {
      await publishAgent({
        name: assistant.name,
        sourceId: assistant.id,
        source: "voxi",
        ...hirevoxOptions,
      });
      await markAssistantPublished(assistant.id);

      toast({
        title: "Assistant Published",
        description: `"${assistant.name}" has been published successfully.`,
      });
      return true;
    } catch (error: any) {
      Sentry.logger.error("Error publishing assistant", {
        error,
        log_source: logSource,
        assistant_id: assistant.id,
        organization_id: currentOrganization?.id,
      });
      toast({
        title: "Publish Failed",
        description: error.message || "Failed to publish assistant",
        variant: "destructive",
      });
      return false;
    } finally {
      setPublishingAssistantId(null);
    }
  };

  return { hirevoxEnabled, publishingAssistantId, publishAssistant };
}
